import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, catchError, shareReplay, throwError } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { ColumnValuesOptions } from './models/dashboard.model';
import { Page } from './models/api-response.model';
import { TenantSelectionService } from '../../components/tenant-select/tenant-select.service';

interface CachedRequest<T> {
  expiresAt: number;
  value$: Observable<T>;
}

/**
 * Service for dashboard-related API calls.
 * Wraps the /dashboard/* endpoints.
 */
@Injectable({ providedIn: 'root' })
export class DashboardApiService {
  private static readonly SMALL_API_CACHE_TTL_MS = 60_000;
  private static readonly LAST_TX_CACHE_TTL_MS = 60_000;
  private static readonly COLUMN_CACHE_MAX_ENTRIES = 200;
  private http = inject(HttpClient);
  private tenantSelection = inject(TenantSelectionService);
  private columnValuesCache = new Map<string, CachedRequest<Page<Record<string, unknown>>>>();
  private lastTransactionDateCache = new Map<
    string,
    CachedRequest<{ last_transaction_date?: string }>
  >();

  /**
   * Get values for a single column with optional distinct mode and ordering.
   */
  getColumnValues(
    columnName: string,
    options?: ColumnValuesOptions
  ): Observable<Page<Record<string, unknown>>> {
    let params = new HttpParams();
    // API expects page/size
    if (options?.page !== undefined) params = params.set('page', String(options.page));
    if (options?.size !== undefined) params = params.set('size', String(options.size));
    if (options?.fetch_distinct !== undefined)
      params = params.set('fetch_distinct', String(options.fetch_distinct));
    if (options?.order_direction) params = params.set('order_direction', options.order_direction);
    if (options?.fetch_all) params = params.set('fetch_all', 'true');
    const request$ = () =>
      this.http.post<Page<Record<string, unknown>>>(
        `${environment.api}/dashboard/data/column/${encodeURIComponent(columnName)}`,
        null,
        { params }
      );
    if (!this.shouldCacheColumnValues(options)) {
      return request$();
    }
    const tenantKey = this.getTenantKeyForCache();
    const cacheKey = `${tenantKey}|${columnName}|${this.serializeColumnOptions(options)}`;
    return this.getOrSetCachedRequest(
      this.columnValuesCache,
      cacheKey,
      request$,
      DashboardApiService.SMALL_API_CACHE_TTL_MS,
      DashboardApiService.COLUMN_CACHE_MAX_ENTRIES
    );
  }

  /**
   * Get the last available transaction date for the current tenant.
   * Endpoint returns an object like: { last_transaction_date: 'YYYY-MM-DD' }
   */
  getLastTransactionDate(tenantKey: string): Observable<{ last_transaction_date?: string }> {
    const scopedTenantKey = this.getTenantKeyForCache(tenantKey);
    const cacheKey = `last-transaction-date|${scopedTenantKey}`;
    return this.getOrSetCachedRequest(
      this.lastTransactionDateCache,
      cacheKey,
      () =>
        this.http.get<{ last_transaction_date?: string }>(
          `${environment.api}/dashboard/last-transaction-date/`
        ),
      DashboardApiService.LAST_TX_CACHE_TTL_MS
    );
  }

  private getTenantKeyForCache(explicitTenantKey?: string): string {
    return (
      (explicitTenantKey && String(explicitTenantKey).trim()) ||
      this.tenantSelection.getCurrentTenantKey()
    );
  }

  private shouldCacheColumnValues(options?: ColumnValuesOptions): boolean {
    if (options?.fetch_all) {
      return false;
    }
    const page = Number.isFinite(options?.page) ? Math.max(0, Math.trunc(options?.page ?? 0)) : 0;
    const size = Number.isFinite(options?.size)
      ? Math.max(1, Math.trunc(options?.size ?? 100))
      : 100;
    // Cache only the first-page lightweight options payloads.
    return page === 0 && size <= 100;
  }

  private serializeColumnOptions(options?: ColumnValuesOptions): string {
    const page = Number.isFinite(options?.page) ? Math.max(0, Math.trunc(options?.page ?? 0)) : 0;
    const size = Number.isFinite(options?.size)
      ? Math.max(1, Math.trunc(options?.size ?? 100))
      : 100;
    const distinct = options?.fetch_distinct === undefined ? 'na' : String(options.fetch_distinct);
    const order = options?.order_direction || 'na';
    const fetchAll = options?.fetch_all ? '1' : '0';
    return `page=${page}&size=${size}&distinct=${distinct}&order=${order}&fetch_all=${fetchAll}`;
  }

  private getOrSetCachedRequest<T>(
    cache: Map<string, CachedRequest<T>>,
    key: string,
    requestFactory: () => Observable<T>,
    ttlMs: number,
    maxEntries?: number
  ): Observable<T> {
    this.evictExpiredCache(cache);
    const now = Date.now();
    const existing = cache.get(key);
    if (existing && existing.expiresAt > now) {
      return existing.value$;
    }

    const value$ = requestFactory().pipe(
      shareReplay({ bufferSize: 1, refCount: false }),
      catchError((error) => {
        cache.delete(key);
        return throwError(() => error);
      })
    );
    cache.set(key, { expiresAt: now + ttlMs, value$ });
    if (maxEntries) {
      this.enforceCacheSizeLimit(cache, maxEntries);
    }
    return value$;
  }

  private evictExpiredCache<T>(cache: Map<string, CachedRequest<T>>): void {
    const now = Date.now();
    for (const [key, entry] of cache.entries()) {
      if (entry.expiresAt <= now) {
        cache.delete(key);
      }
    }
  }

  private enforceCacheSizeLimit<T>(cache: Map<string, CachedRequest<T>>, maxEntries: number): void {
    while (cache.size > maxEntries) {
      const firstKey = cache.keys().next().value as string | undefined;
      if (!firstKey) return;
      cache.delete(firstKey);
    }
  }
}
